/**
 * Two-stage flow for the "Offboard a new asset" toolbar action on the
 * Offboard Asset page. Stage 1 lets the user search active Atlas assets;
 * picking one transitions to stage 2 which is the regular
 * `AssetOffboardDialog` form (outcome / reason / additional info / IoT
 * notify). Same submit endpoint and behaviour as right-clicking an asset
 * on the Assets report, just an alternate entry point.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { AssetOffboardDialog } from './asset-offboard-dialog';

interface AssetRow {
  id: string;
  fleetNumber?: string | null;
  registrationNumber?: string | null;
  make?: string | null;
  model?: string | null;
  vehicleStatus?: string | null;
  trackingProvider?: string | null;
}

export function AssetOffboardPickerDialog({
  onClose, onComplete,
}: {
  onClose: () => void;
  onComplete?: () => void;
}) {
  const [search, setSearch] = React.useState('');
  const [picked, setPicked] = React.useState<AssetRow | null>(null);

  // 200-row cap keeps the picker fast — the Active Atlas fleet is in this
  // ballpark and the search filter narrows further client-side. Larger
  // fleets should rely on the search box.
  const { data, isLoading } = useQuery({
    queryKey: ['offboard-picker', search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: '1', limit: '200' });
      if (search.trim()) params.set('search', search.trim());
      const r = await api<AssetRow[]>(`/fleet/assets?${params.toString()}`);
      return r.data || [];
    },
    enabled: !picked,
  });

  // Stage 2 — once an asset is picked, render the existing offboard form
  // unchanged. Its onComplete bubbles up so the picker dialog closes and
  // the parent (CrudPage) refetches the offboarding history table.
  if (picked) {
    return (
      <AssetOffboardDialog
        row={picked}
        onClose={onClose}
        onComplete={onComplete}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Offboard a new asset</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search by Fleet # / Rego / Make / Model"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <div className="border rounded-md max-h-[420px] overflow-auto">
            {isLoading ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Loading…</div>
            ) : (data ?? []).length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">
                {search ? 'No active assets match that search.' : 'No active assets.'}
              </div>
            ) : (
              <ul className="divide-y">
                {(data ?? []).map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(a)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent text-left text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {a.fleetNumber || '—'}
                          {a.registrationNumber && (
                            <span className="text-muted-foreground ml-2 text-xs">({a.registrationNumber})</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {[a.make, a.model].filter(Boolean).join(' ') || '—'}
                          {a.trackingProvider && <> · IoT: {a.trackingProvider}</>}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Pick the active asset you want to offboard. The offboarding form opens next.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
